import {
  Button,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  Chip,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { LicenseModel } from 'types'

export default function LicenseAssignPage() {
  const { login } = useParams<{ login: string }>()
  const navigate = useNavigate()
  const [availableLicenses, setAvailableLicenses] = useState<LicenseModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedLicenseKey, setSelectedLicenseKey] = useState<string | null>(
    null
  )

  useEffect(() => {
    const token = localStorage.getItem('token')
    axios
      .get(`${base_url}/license/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        // Filter for licenses that are not assigned to any user
        const unassigned = res.data.filter(
          (lic: LicenseModel) => !lic.invitedUserLogin
        )
        setAvailableLicenses(unassigned)
      })
      .catch((err) => {
        console.error('Failed to load licenses:', err)
        alert('Impossible de charger la liste des licences.')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleSubmit = () => {
    if (!selectedLicenseKey) {
      alert('Veuillez sélectionner une licence.')
      return
    }

    setIsSaving(true)
    const token = localStorage.getItem('token')
    const payload = { licenseKey: selectedLicenseKey }

    axios
      .post(`${base_url}/user/${login}/license`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert('Licence assignée avec succès !')
        navigate(`/user/${login}`)
      })
      .catch((error) => {
        console.error('Failed to assign license:', error)
        alert(
          "L'assignation a échoué: " +
            (error.response?.data?.message || 'Erreur inconnue')
        )
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement des licences...">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Assigner une licence à ${login}`}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '800px',
          margin: '0 auto',
        }}
      >
        <Typography variant="h5">
          Sélectionner une licence disponible
        </Typography>
        <Paper sx={{ width: '100%', maxHeight: '60vh', overflow: 'auto' }}>
          <List>
            {availableLicenses.length > 0 ? (
              availableLicenses.map((lic, index) => (
                <Box key={lic.licenseKey}>
                  <ListItem
                    button
                    selected={selectedLicenseKey === lic.licenseKey}
                    onClick={() => setSelectedLicenseKey(lic.licenseKey)}
                  >
                    <ListItemText
                      primary={lic.licenseKey}
                      secondary={
                        <Chip
                          label={
                            lic.isMedical
                              ? 'Licence Médicale'
                              : 'Licence Standard'
                          }
                          size="small"
                          color={lic.isMedical ? 'info' : 'default'}
                        />
                      }
                    />
                  </ListItem>
                  {index < availableLicenses.length - 1 && <Divider />}
                </Box>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="Aucune licence disponible n'a été trouvée." />
              </ListItem>
            )}
          </List>
        </Paper>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving || !selectedLicenseKey}
          sx={{ mt: 2, minWidth: '200px' }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Confirmer la licence'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
